import { Button } from '@ipfabric/ui-components/Button/Button';
import { ScrollView } from '@ipfabric/ui-components/ScrollView/ScrollView';
import {
  makeSelectDiscoveryRunByUser,
  makeSelectDiscoveryStarting,
} from 'containers/App/Header/JobManagement/selectors';
import { showWarning } from 'containers/Toaster/actions';
import { Map as ImmutableMap } from 'immutable';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { merge } from 'ramda';
import { PureComponent } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import handleEvent from 'utils/handleEvent';
import { withAppProps } from 'utils/react/withAppProps';
import { SnapshotsList } from './SnapshotsList/SnapshotsList';
import Upload from './Upload';
import {
  clearTabs,
  discoveryProcessStart,
  discoveryProcessStop,
  setActiveMenuTab,
  uploadSnapshot,
} from './actions';
import { isSnapshotLoaded, isSnapshotUnloaded } from './helpers';
import {
  makeSelectDisableStartBtn,
  makeSelectUploadDialogueError,
  makeSelectUploadingSnapshot,
} from './selectors';

@withAppProps
class Menu extends PureComponent {
  static propTypes = {
    auth: PropTypes.object.isRequired,
    currentSnapshotId: ImmutablePropTypes.list.isRequired,
    discoveryRunByUser: PropTypes.bool.isRequired,
    discoveryStarting: PropTypes.bool.isRequired,
    filteredSnapshots: ImmutablePropTypes.list,
    isStartBtnDisabled: PropTypes.bool.isRequired,
    msg: PropTypes.object.isRequired,
    navigate: PropTypes.func.isRequired,
    onAddMultipleItems: PropTypes.func.isRequired,
    onClearTabs: PropTypes.func.isRequired,
    onSelectSite: PropTypes.func.isRequired,
    onShowWarning: PropTypes.func.isRequired,
    onUploadSnapshot: PropTypes.func.isRequired,
    selectedSite: ImmutablePropTypes.list.isRequired,
    uploadDialogueError: PropTypes.bool,
    uploadingSnapshot: PropTypes.bool.isRequired,
  };

  getSnapshotsList = (snapshots) => {
    const { msg } = this.props;
    const snapshotsLoaded = snapshots.filter((snapshot) =>
      isSnapshotLoaded(snapshot.get('state')),
    );
    const snapshotsUnloaded = snapshots.filter((snapshot) =>
      isSnapshotUnloaded(snapshot.get('state')),
    );
    return new ImmutableMap({
      loaded: new ImmutableMap({
        name: get(msg, 'loadedSnapshotsTitle'),
        children: snapshotsLoaded,
      }),
      unloaded: new ImmutableMap({
        name: get(msg, 'unloadedSnapshotsTitle'),
        children: snapshotsUnloaded,
      }),
    });
  };

  handleNewClick = (disabled) =>
    handleEvent((e) => {
      if (disabled && e && e.preventDefault) {
        e.preventDefault();
      }
      const { onClearTabs } = this.props;
      onClearTabs();
      if (document.activeElement) {
        document.activeElement.blur();
      }
      this.props.navigate('/snapshot-management/new');
      return false;
    });

  handleRemoveSite = handleEvent((e) => {
    const { onSelectSite, selectedSite } = this.props;
    const siteToRemove = e.target.getAttribute('data-id');
    const foundIndex = selectedSite.findIndex(
      (site) => site.get('uid') === siteToRemove,
    );
    if (foundIndex > -1) {
      onSelectSite(selectedSite.delete(foundIndex));
    }
  });

  handleUploadSnapshot = handleEvent((args) => {
    const { onUploadSnapshot, currentSnapshotId } = this.props;
    onUploadSnapshot(
      merge(args, { currentSnapshotId: currentSnapshotId.get(0) }),
    );
  });

  render() {
    const {
      auth,
      currentSnapshotId,
      discoveryRunByUser,
      discoveryStarting,
      filteredSnapshots,
      isStartBtnDisabled,
      msg,
      onAddMultipleItems,
      onShowWarning,
      selectedSite,
      uploadDialogueError,
      uploadingSnapshot,
    } = this.props;
    const snapshotsList = this.getSnapshotsList(filteredSnapshots);
    const hasWritePermission = auth.scope.includes('write');
    const startButtonDisabled =
      discoveryStarting ||
      isStartBtnDisabled ||
      !hasWritePermission ||
      discoveryRunByUser ||
      currentSnapshotId.includes('new');
    return (
      <div className='snapshots-leftwrapper showpanel flex flex-col overflow-y-auto'>
        <div
          className={`${currentSnapshotId.includes('new') ? 'snapshots-menu__item--active' : ''}`}
        >
          <div className='flex m-4 gap-4'>
            <Button
              onClick={this.handleNewClick(startButtonDisabled)}
              icon='fa-plus-circle'
              size='default'
              variant='primary'
              disabled={startButtonDisabled}
              className='grow'
            >
              {get(msg, 'startButton.newSnapshot')}
            </Button>
            <Upload
              error={uploadDialogueError}
              msg={msg}
              onShowWarning={onShowWarning}
              onUploadSnapshot={this.handleUploadSnapshot}
              hasWritePermission
              uploading={uploadingSnapshot}
            />
          </div>
        </div>
        {selectedSite.size ? (
          <fieldset className='snapshots-menu__fieldset'>
            <legend className='snapshots-menu__legend snapshots-menu__item'>
              {get(msg, 'sitesFilter')}
            </legend>
            <div className='pl-lgu-6 pr-lgu-6 snapshots-menu__item flow-root'>
              {selectedSite
                .map((site) => (
                  <span
                    key={site}
                    className='label label-primary mr-lgu-2 mt-lgu-2 float-left inline-block cursor-pointer leading-tight'
                    onClick={this.handleRemoveSite}
                    data-id={site.get('uid')}
                  >
                    {site.get('siteName')}
                  </span>
                ))
                .toJS()}
            </div>
          </fieldset>
        ) : null}
        <ScrollView>
          <div className='flex flex-1 flex-col'>
            <SnapshotsList
              snapshotsList={snapshotsList.toJS()}
              selectedSnapshot={currentSnapshotId.toJS() ?? null}
              onAddSelectedItems={onAddMultipleItems}
              msg={msg}
            />
            <div className='snapshots-menu__snapshot-space snapshots-menu__item flex-1' />

            <div className='snapshots-leftwrapper__bottom snapshots-menu__item pb-lgu-5' />
          </div>
        </ScrollView>
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  discoveryRunByUser: makeSelectDiscoveryRunByUser(),
  discoveryStarting: makeSelectDiscoveryStarting(),
  isStartBtnDisabled: makeSelectDisableStartBtn(),
  uploadDialogueError: makeSelectUploadDialogueError(),
  uploadingSnapshot: makeSelectUploadingSnapshot(),
});

function mapDispatchToProps(dispatch) {
  return {
    onClearTabs: () => dispatch(clearTabs()),
    onShowWarning: (args) => dispatch(showWarning(args)),
    onUploadSnapshot: (args) => dispatch(uploadSnapshot(args)),
    onDiscoveryProcessStart: (args) => dispatch(discoveryProcessStart(args)),
    onDiscoveryProcessStop: (args) => dispatch(discoveryProcessStop(args)),
    onSetActiveMenuTab: (args) => dispatch(setActiveMenuTab(args)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Menu);
